import React, { useEffect, useState } from "react";
import { styled } from '@mui/material/styles';
import Radio, { RadioProps } from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { ContentURL, getContentUrl } from "../../Utils/ContentURL";
import cn from "classnames";
import { Dispatch } from "../../Dispatch";
import { NoticeType } from "./Notifications/NotificationEntities";
import { PaymentOption, PaymentOptionKind } from "../../Payment/PaymentEntities";
import { CheckForCardExpiry } from "../../Payment/PaymentCardValidation";
import { useAppState } from "../../../Redux/ReduxHooks";
import { BookingDataOwnership, BookingStatus } from "../../../Services/BookingEntities";
import { Separator } from "./Separator";
import appstore from "../../../appStore";
import { VoiceBookingUpdateState } from "../Redux/PhoneToCnpState";
import { MyStorage } from "../../../Storage";

/**
 * Component to choose between meter fare and Fixed Price.
 */
export const BookingFareSection: React.FC = () => {

    const [fixedFareTimerId, setFixedFareTimerId] = useState<NodeJS.Timeout | null>(null);
    const isFixedFareSelected = useAppState(state => state.PhoneToCnp.IsFixedFareSelected);
    const isPaymentConfirmed = useAppState(state => state.PhoneToCnp.IsPaymentConfirmed);
    const bookingDetails = useAppState(state => state.PhoneToCnp.Booking)!;
    const paymentUpdateState = useAppState(state => state.PhoneToCnp.PaymentUpdateState);

    const [fareType, setFareType] = useState(isFixedFareSelected ? 'fixed' : 'meter');
    
    function UpdateFareType(e: React.ChangeEvent<HTMLInputElement>) {
        setFareType(e.target.value);

        if (e.target.value === 'fixed') {
            Dispatch.PhoneToCnp.SetFixedFare();

            // Auto-select the saved card when switching to fixed price, if one is present.
            // Prefer the card already attached to the booking (Payment); fall back to the return user's saved card (SavedCard).
            const payment = bookingDetails.Payment ?? bookingDetails.SavedCard;
            if (payment) {
                const existingCardOption: PaymentOption = {
                    Id: payment.CardId,
                    Name: payment.CardNumber, // Show masked card number in the preselection (e.g. ****1234) instead of the card nickname
                    Type: payment.CardType,
                    IsDefault: false,
                    Kind: PaymentOptionKind.CardOrWallet,
                    Card: {
                        CardId: payment.CardId,
                        CardNumber: payment.CardNumber,
                        CardType: payment.CardType,
                        CardName: payment.CardName,
                        CardExpiry: payment.CardExpiry,
                        IsDefault: false,
                        IsBlocked: false,
                        CheckFor3Ds: false // 3DS disabled for existing cards in phone to CNP flow
                    }
                };
                Dispatch.PhoneToCnp.PaymentOption(existingCardOption);
                CheckForCardExpiry(existingCardOption);
            }
        }
        else {
            Dispatch.PhoneToCnp.ClearFixedFare();
        }
    }
    
    useEffect(() => {
        if (fareType === "fixed") {
            StartFixedFareTimer();
        }
        else if (fareType === "meter") {
            if (fixedFareTimerId) {
                clearTimeout(fixedFareTimerId);
                setFixedFareTimerId(null);
            }
        }

        // Cleanup function clears the timer on unmount
        return () => {
            if (fixedFareTimerId) {
                clearTimeout(fixedFareTimerId);
            }
        }
    }, [fareType, isPaymentConfirmed, paymentUpdateState, bookingDetails?.TrackingInfo.BookingStatusID]);

    /** Trigger a notice when the user has selected the fixed price option but has not completed the payment yet after 30 seconds. */
    function StartFixedFareTimer() {
        const timerId = setTimeout(() => {            

            // Read the latest value from the store to avoid a stale closure over isPaymentConfirmed.
            if (!appstore.getState().PhoneToCnp.IsPaymentConfirmed && appstore.getState().PhoneToCnp.PaymentUpdateState !== VoiceBookingUpdateState.InProgress && appstore.getState().PhoneToCnp.PaymentUpdateState !== VoiceBookingUpdateState.FinishedSuccess && appstore.getState().PhoneToCnp.Booking!.TrackingInfo.BookingStatusID < BookingStatus.PickedUp) {
                Dispatch.PhoneToCnp.ShowNotice(NoticeType.FixedPriceNotLockedIn);
            }
        }, 30000);
        setFixedFareTimerId(timerId);
    }

    if (isPaymentConfirmed || bookingDetails.Payment) return null;
    if (bookingDetails?.TrackingInfo.BookingStatusID >= BookingStatus.PickedUp) return null;

    // User's with read-only access (shared booking) can't view this section.
    if (bookingDetails.DataOwnership === BookingDataOwnership.ReadOnlyLink) return null;

    return (
        <>
            <Separator />
            <div className="booking-fare-section">                
                <div className="booking-fare-title">Lock in a Fixed Price</div>
                <div className="booking-fare-info">Pay now with price certainty and exit cab faster.</div>
                <FormControl>
                    <RadioGroup
                        value={fareType}
                        aria-labelledby="customized-radios"
                        name="customized-radios"
                        onChange={UpdateFareType}
                    >
                        <div
                            className={cn("fare-option-group", { "fare-selected": fareType === "meter" })}
                            onClick={(e) => UpdateFareType({ target: { value: 'meter' } } as React.ChangeEvent<HTMLInputElement>)}>
                            <div className="radio-button-and-label">
                                <FormControlLabel value="meter" control={<BpRadio />} label="" />
                                <div className="fare-option">
                                    <div>
                                        <div className="fare-type">Estimated metered fare</div>
                                        <div className="fare-type-description">Pay in taxi - price may vary</div>
                                    </div>
                                </div>
                            </div>
                            <div className="fare-amount">{`$${bookingDetails.Fare.LikelyMinDollarsMetered} - $${bookingDetails.Fare.LikelyMaxDollarsMetered}`}</div>
                        </div>
                        <div
                            className={cn("fare-option-group fixed-fare", { "fare-selected": fareType === "fixed" })}
                            onClick={(e) => UpdateFareType({ target: { value: 'fixed' } } as React.ChangeEvent<HTMLInputElement>)}>
                            <div className="radio-button-and-label">
                                <FormControlLabel value="fixed" control={<BpRadio />} label="" />
                                <div className="fare-option">
                                    <div>
                                        <div className="fare-type">Fixed Price</div>
                                        <div className="fare-type-description">Pay now - fare locked in</div>
                                    </div>
                                </div>
                            </div>
                            <div className="fare-amount">
                                <img className="lock-icon" src={getContentUrl(ContentURL.images.PhoneToCnp.LockIcon)}></img>
                                <span>{`$${bookingDetails.Fare.FixedPriceDollars.toFixed(2)}`}</span>
                            </div>
                        </div>
                    </RadioGroup>
                </FormControl>
            </div>
        </>                
    )
}

/** Adding custom styles to the radio buttons. */
const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '.Mui-focusVisible &': {
        outline: '2px auto rgba(19,124,189,.6)',
        outlineOffset: 2,
    },
    'input:hover ~ &': {
        backgroundColor: '#804200'
    },
    'input:disabled ~ &': {
        boxShadow: 'none',
        background: 'rgba(206,217,224,.5)'
    },
}));

const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: '#804200',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&::before': {
        display: 'block',
        width: 16,
        height: 16,
        backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
        content: '""',
    }
});

function BpRadio(props: RadioProps) {
    return (
        <Radio
            disableRipple
            color="default"
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            {...props}
        />
    );
}
